<script setup lang="ts">
import {
  tpgIconPerson,
  tpgIconPolyline,
  tpgIconQuestionMark,
  tpgIconRoute,
  tpgIconSensors,
  tpgIconTimeline,
} from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  GrammarBadge,
  GrammarFooter,
  GrammarModal,
  GrammarPrimaryButton,
  GrammarTextField,
} from '@nora-monorepo/grammar-ui';
import { GeofenceRuleType } from '@nora-monorepo/nora-api-client';
import { getUserDisplayName } from '@nora-monorepo/shared';
import { textQuaternary } from '@nora-monorepo/ui-shared';

import { useGeofenceModalStore } from '#nora-ui/components/modals/geofence-modal/geofence-modal-store';
import MiniMap from '#nora-ui/components/standalone-maps/MiniMap.vue';
import { useOperationStore } from '#nora-ui/stores/operation-store';

const { t, d } = useI18n();
const geofenceModalStore = useGeofenceModalStore();
const operationStore = useOperationStore();

const { currentGeofenceRule, geofenceTriggerTime, isShowingGeofenceModal, isError }
  = storeToRefs(geofenceModalStore);

const geofenceOperationName = computed(() => {
  return operationStore.getOperation(currentGeofenceRule.value?.operationId)?.name;
});

const isOperationDeleted = computed(() => {
  return operationStore.getOperation(currentGeofenceRule.value?.operationId)?.deletedAt;
});

function handleClose() {
  geofenceModalStore.closeModal();
}

const affectedUsers = computed(() => {
  const rule = currentGeofenceRule.value;
  if (!rule?.operationId || !rule?.ruleTargetUsers) {
    return [];
  }
  const users = operationStore.getOperationUsers(rule.operationId);
  const targetUserIds = new Set(rule.ruleTargetUsers);
  return users.filter(user => targetUserIds.has(user.user.id));
});

function iconPickerForRule() {
  if (!currentGeofenceRule.value) {
    return tpgIconQuestionMark;
  }
  switch (currentGeofenceRule.value.type) {
    case GeofenceRuleType.IDLE_LOCATION:
    case GeofenceRuleType.NO_LOCATION:
    case GeofenceRuleType.DIVERGE:
    case GeofenceRuleType.CONVERGE:
      return tpgIconSensors;
    case GeofenceRuleType.CROSS_LINE:
      return tpgIconTimeline;
    case GeofenceRuleType.DEVIATE:
      return tpgIconRoute;
    default:
      return tpgIconPolyline;
  }
}
</script>

<template>
  <div
    class="flex"
    :class="[textQuaternary]"
  >
    <GrammarModal
      :is-open="isShowingGeofenceModal"
      :show-close-button="true"
      :icon-template="iconPickerForRule()"
      :label="
        isError
          ? 'Could not load rule information'
          : currentGeofenceRule?.name
      "
      :secondary-label="geofenceTriggerTime ? `Rule triggered at time: ${d(geofenceTriggerTime, 'long')}` : undefined"
      @close="handleClose"
    >
      <template #content>
        <div
          v-if="!isError && currentGeofenceRule"
          class="flex flex-col gap-4 mb-4"
        >
          <GrammarTextField
            :read-only="true"
            label="Belongs to operation"
            :value="geofenceOperationName"
          />
          <GrammarTextField
            :read-only="true"
            label="Rule Name"
            :value="currentGeofenceRule.name"
          />
          <GrammarTextField
            v-if="currentGeofenceRule.description"
            :read-only="true"
            label="Description"
            :value="currentGeofenceRule.description"
          />
          <GrammarTextField
            :read-only="true"
            label="Rule priority"
            :value="currentGeofenceRule.severity"
          />
          <GrammarTextField
            :read-only="true"
            label="Rule status"
            :value="currentGeofenceRule.active ? 'Active' : 'Not active'"
          />
          <GrammarTextField
            :read-only="true"
            label="Rule Type"
            :value="currentGeofenceRule.type"
          />
          <div>
            <GrammarBadge label="Affected users" />
            <div
              v-for="affectedUser in affectedUsers"
              :key="affectedUser.user.id"
            >
              <GrammarBadge
                emphasis="no-background"
                :leading-icon-template="tpgIconPerson"
                :label="getUserDisplayName(affectedUser.user)"
              />
            </div>
          </div>

          <p v-if="currentGeofenceRule.feature">
            <MiniMap
              :geojson-feature="currentGeofenceRule.feature as GeoJSON.Feature"
              :width="320"
              :height="200"
            />
          </p>
          <div
            v-if="isOperationDeleted !== null"
            class="flex justify-center pt-4"
          >
            <GrammarBadge
              size="xl"
              label="Operation deleted"
              emphasis="high"
              type="warning"
            />
          </div>
          <div
            v-if="currentGeofenceRule.deletedAt !== null"
            class="flex justify-center pt-4"
          >
            <GrammarBadge
              size="xl"
              label="Geofence rule deleted"
              emphasis="high"
              type="warning"
            />
          </div>
        </div>
      </template>

      <template #footer>
        <GrammarFooter>
          <template #right-button>
            <GrammarPrimaryButton
              :label="t('common.close')"
              @click="handleClose"
            />
          </template>
        </GrammarFooter>
      </template>
    </GrammarModal>
  </div>
</template>
